import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import { Box, Divider, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { FONT_SIZE, INK, OUTREACH_POPUP, RADIUS, SURFACE } from "../../../theme/tokens";
import { AccentIconTile } from "../../UIToolkit/molecules/AccentIconTile";

export type OutreachApprovalTone = "success" | "warning";

type Props = {
	tone: OutreachApprovalTone;
	icon: ReactNode;
	/** What happened; the live region, so a decision is announced. */
	message: ReactNode;
	/** Kept outside the live region so a re-render never re-reads the controls. */
	actions: ReactNode | null;
	footer: ReactNode | null;
};

/** The approval callout's frame: a toned wash with its icon, the message and actions on one row, and a footer under a hairline. */
export const OutreachApprovalShell: FC<Props> = ({ tone, icon, message, actions, footer }) => {
	const theme = useTheme();
	const accent = theme.palette[tone].main;
	const edge = alpha(accent, OUTREACH_POPUP.APPROVAL_BORDER_ALPHA);
	return (
		<Box
			data-testid="outreach-approval"
			data-tone={tone}
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1.25,
				px: 1.75,
				py: 1.5,
				borderRadius: RADIUS.CONTROL / RADIUS.CARD,
				border: `1px solid ${edge}`,
				backgroundColor: alpha(accent, OUTREACH_POPUP.APPROVAL_WASH_ALPHA),
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
				<AccentIconTile size="sm" color={SURFACE} tint={accent}>
					{icon}
				</AccentIconTile>
				<Box role="status" sx={{ flex: "1 1 280px", minWidth: 0, display: "flex" }}>
					{message}
				</Box>
				{actions}
			</Box>
			{footer !== null && (
				<>
					<Divider sx={{ borderColor: edge }} />
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: FONT_SIZE.CAPTION, color: INK.SECONDARY }}>
						<BoltRoundedIcon fontSize="small" sx={{ color: "primary.main" }} />
						{footer}
					</Box>
				</>
			)}
		</Box>
	);
};
